<template>
    <div class="body taraz-analysis-app">
        <Navbar :show-back-button="true" @back="navigateToDashboard"/>
        
        <div class="section-header" style="direction: rtl; margin-top: 10px;">
            <!-- <button class="back-arrow" @click="navigateToDashboard">
                →
            </button> -->
            <h3 class="section-title mt-3">{{ $t('employer.trazAnalysis.title') }}</h3>
            <p class="section-subtitle">{{ $t('employer.trazAnalysis.subtitle') }}</p>
        </div>
        
        <!-- Main Content -->
        <div class="main-content">
            <!-- Left Panel: Controls and Analysis Settings -->
            <div class="left-panel">
                <!-- Analysis Controls Card -->
                <div class="control-card">
                    <div class="card-header">
                        <h4>{{ $t('employer.trazAnalysis.analysis_settings') }}</h4>
                    </div>
                    
                    <div class="card-body" style="direction: rtl;">
                        <!-- Currency Selector -->
                        <div class="form-group">
                            <label class="form-label">{{ $t('employer.trazAnalysis.currency') }}</label>
                            <select 
                                v-model="selectedArz" 
                                @change="onArzChange"
                                class="form-select"
                                :disabled="loadingArz || arzList.length === 0"
                            >
                                <option 
                                    v-for="arz in arzList" 
                                    :key="arz.idd" 
                                    :value="arz.idd"
                                >
                                    {{ arz.nam }} ({{ arz.sign }})
                                </option>
                            </select>
                            <div v-if="loadingArz" class="loading-text">
                                {{ $t('employer.trazAnalysis.loading_currencies') }}
                            </div>
                        </div>
                        
                        <!-- Month Selector -->
                        <div class="form-group">
                            <label class="form-label">{{ $t('employer.trazAnalysis.month') }}</label>
                            <select 
                                v-model="selectedMonth" 
                                @change="onMonthChange"
                                class="form-select"
                            >
                                <option value="">{{ $t('employer.trazAnalysis.select_month') }}</option>
                                <option v-for="(month, index) in persianMonths" 
                                        :key="index" 
                                        :value="index + 1">
                                    {{ month }}
                                </option>
                            </select>
                        </div>

                        <!-- CM Level Selector -->
                        <div class="form-group">
                            <label for="cm-select" class="form-label">{{ $t('employer.trazAnalysis.display_level') }}</label>
                            <select 
                                id="cm-select"
                                v-model="selectedCmLevel" 
                                @change="onCmLevelChange"
                                class="form-select"
                                :disabled="loadingArz || arzList.length === 0"
                            >
                                <option value="1">{{ $t('employer.trazAnalysis.level_group') }}</option>
                                <option value="2">{{ $t('employer.trazAnalysis.level_total') }}</option>
                                <option value="3">{{ $t('employer.trazAnalysis.level_sub') }}</option>
                                <option value="4">{{ $t('employer.trazAnalysis.level_detail') }}</option>
                            </select>
                            <div v-if="loadingArz" class="currency-loading">
                                {{ $t('employer.trazAnalysis.loading_currencies') }}
                            </div>
                        </div>
                        
                        <!-- Year Selector -->
                        <div class="form-group">
                            <label class="form-label">{{ $t('employer.trazAnalysis.year') }}</label>
                            <input 
                                type="number" 
                                v-model="selectedYear" 
                                @change="onYearChange"
                                class="form-input"
                                :placeholder="$t('employer.trazAnalysis.year_placeholder')"
                                min="1300"
                                max="1500"
                            />
                        </div>
                        
                        <!-- Analysis Type Selector -->
                        <div class="form-group">
                            <label class="form-label">{{ $t('employer.trazAnalysis.analysis_type') }}</label>
                            <div class="analysis-types-controls">
                                <div class="analysis-buttons">
                                    <button 
                                        @click="selectAllAnalysisTypes" 
                                        class="btn-select-all"
                                    >
                                        {{ $t('employer.trazAnalysis.select_all') }}
                                    </button>
                                    <button 
                                        @click="deselectAllAnalysisTypes" 
                                        class="btn-deselect-all"
                                    >
                                        {{ $t('employer.trazAnalysis.deselect_all') }}
                                    </button>
                                </div>
                                
                                <div class="analysis-types-grid">
                                    <div 
                                        v-for="type in analysisTypes" 
                                        :key="type.id"
                                        class="analysis-type-item"
                                        :class="{ 'selected': type.checked }"
                                        @click="toggleAnalysisType(type.id)"
                                    >
                                        <div class="analysis-type-checkbox">
                                            <span v-if="type.checked" class="checkbox-checked">✓</span>
                                            <span v-else class="checkbox-unchecked">□</span>
                                        </div>
                                        <div class="analysis-type-label">
                                            {{ type.label }}
                                        </div>
                                    </div>
                                </div>
                                
                                <div class="selected-types-summary">
                                    <small>{{ $t('employer.trazAnalysis.selected_count', { count: selectedAnalysisTypesCount, total: analysisTypes.length }) }}</small>
                                    <div class="selected-types-tags">
                                        <span 
                                            v-for="type in analysisTypes.filter(t => t.checked)" 
                                            :key="type.id"
                                            class="type-tag"
                                        >
                                            {{ type.label }}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>
                        
                        <!-- Language Selector -->
                        <div class="form-group">
                            <label class="form-label">{{ $t('employer.trazAnalysis.analysis_language') }}</label>
                            <div class="language-buttons">
                                <button 
                                    @click="setLanguage('fa')" 
                                    :class="['lang-btn', targetLanguage === 'fa' ? 'active' : '']"
                                >
                                    {{ $t('employer.trazAnalysis.lang_persian') }}
                                </button>
                                <button 
                                    @click="setLanguage('en')" 
                                    :class="['lang-btn', targetLanguage === 'en' ? 'active' : '']"
                                >
                                    {{ $t('employer.trazAnalysis.lang_english') }}
                                </button>
                            </div>
                        </div>
                        
                        <!-- User Query Input -->
                        <div class="form-group">
                            <label class="form-label">{{ $t('employer.trazAnalysis.query_label') }}</label>
                            <textarea 
                                v-model="userQuery" 
                                class="form-textarea" 
                                :placeholder="$t('employer.trazAnalysis.query_placeholder')"
                                rows="3"
                            ></textarea>
                            <div class="query-examples">
                                <small>{{ $t('employer.trazAnalysis.example_questions') }}</small>
                                <div class="example-tags">
                                    <span @click="setExampleQuery($t('employer.trazAnalysis.example_liquidity'))">{{ $t('employer.trazAnalysis.example_liquidity') }}</span>
                                    <span @click="setExampleQuery($t('employer.trazAnalysis.example_profitability'))">{{ $t('employer.trazAnalysis.example_profitability') }}</span>
                                    <span @click="setExampleQuery($t('employer.trazAnalysis.example_financial_ratios'))">{{ $t('employer.trazAnalysis.example_financial_ratios') }}</span>
                                </div>
                            </div>
                        </div>
                        
                        <!-- Action Buttons -->
                        <div class="action-buttons">
                            <button 
                                @click="fetchDataAndAnalyze" 
                                :disabled="loadingAnalysis || !selectedMonth || !selectedYear"
                                class="btn-analyze"
                            >
                                <span v-if="loadingAnalysis" class="loading-spinner-small"></span>
                                <span v-else>{{ $t('employer.trazAnalysis.analyze_button') }}</span>
                                {{ loadingAnalysis ? $t('employer.trazAnalysis.analyzing') : '' }}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
            
            <!-- Right Panel: Results -->
            <div class="right-panel">
                <!-- Loading State -->
                <div v-if="loadingAnalysis || loadingTaraz" class="loading-state">
                    <div class="loading-spinner-large"></div>
                    
                    <!-- Dynamic Loading Text for Analysis -->
                    <div v-if="loadingAnalysis" class="loading-analysis-steps">
                        <p class="loading-step active">{{ currentLoadingStep }}</p>
                        
                        <!-- Progress Bar -->
                        <div class="progress-container">
                            <div 
                                class="progress-bar" 
                                :style="{ width: progressPercentage + '%' }"
                            ></div>
                            <div class="progress-text">{{ Math.round(progressPercentage) }}%</div>
                        </div>
                        
                        <!-- Step Indicators -->
                        <div class="step-indicators">
                            <div 
                                v-for="(step, index) in loadingSteps" 
                                :key="index"
                                :class="['step-dot', stepIndex >= index ? 'active' : '']"
                                :title="step"
                            ></div>
                        </div>
                        
                        <!-- Estimated Time -->
                        <div class="estimated-time">
                            ⏱️ {{ $t('employer.trazAnalysis.estimated_time') }}: {{ estimatedTime }} {{ $t('employer.trazAnalysis.seconds') }}
                        </div>
                        
                        <!-- AI Models Status -->
                        <div class="ai-models-status">
                            <div class="models-loading">
                                <span class="model-icon">🤖</span>
                                <span class="model-text">{{ $t('employer.trazAnalysis.using_ai_models', { count: activeModels }) }}</span>
                            </div>
                        </div>
                    </div>
                    
                    <!-- Simple Loading for Taraz Data -->
                    <div v-else class="loading-taraz">
                        <p>{{ $t('employer.trazAnalysis.loading_taraz_data') }}</p>
                        <div class="loading-detail">
                            <p>📊 {{ $t('employer.trazAnalysis.loading_financial_data') }}</p>
                            <p>⏳ {{ $t('employer.trazAnalysis.loading_quick') }}</p>
                        </div>
                    </div>
                    
                    <!-- Tips while loading -->
                    <div class="loading-tips">
                        <p class="tips-title">💡 {{ $t('employer.trazAnalysis.tip') }}:</p>
                        <p class="tip-text">{{ randomLoadingTip }}</p>
                    </div>
                </div>
                
                <!-- Error State -->
                <div v-if="error" class="error-state">
                    <div class="error-icon">⚠️</div>
                    <h4>{{ $t('employer.trazAnalysis.error_title') }}</h4>
                    <p>{{ error }}</p>
                    <button @click="clearError" class="btn-retry">{{ $t('employer.trazAnalysis.retry') }}</button>
                </div>
                
                <!-- Taraz Data Summary (if available) -->
                <div v-if="tarazData.length > 0 && !loadingAnalysis" class="summary-card">
                    <div class="card-header">
                        <h4>📋 {{ $t('employer.trazAnalysis.taraz_summary') }}</h4>
                        <span class="badge">{{ tarazData.length }} {{ $t('employer.trazAnalysis.accounts') }}</span>
                    </div>
                    
                    <div class="card-body">
                        <div class="summary-grid">
                            <div class="summary-item">
                                <span class="summary-label">{{ $t('employer.trazAnalysis.date') }}:</span>
                                <span class="summary-value">{{ selectedMonthName }} {{ selectedYear }}</span>
                            </div>
                            <div class="summary-item">
                                <span class="summary-label">{{ $t('employer.trazAnalysis.currency_label') }}:</span>
                                <span class="summary-value">{{ selectedArzName }}</span>
                            </div>
                            <div class="summary-item">
                                <span class="summary-label">{{ $t('employer.trazAnalysis.total_assets') }}:</span>
                                <span class="summary-value debit">{{ formatCurrency(calculatedTotals.totalAssets) }}</span>
                            </div>
                            <div class="summary-item">
                                <span class="summary-label">{{ $t('employer.trazAnalysis.total_liabilities') }}:</span>
                                <span class="summary-value credit">{{ formatCurrency(calculatedTotals.totalLiabilities) }}</span>
                            </div>
                            <div class="summary-item">
                                <span class="summary-label">{{ $t('employer.trazAnalysis.total_equity') }}:</span>
                                <span class="summary-value">{{ formatCurrency(calculatedTotals.totalEquity) }}</span>
                            </div>
                        </div>
                        
                        <button @click="showTarazTable = !showTarazTable" class="btn-toggle-table">
                            {{ showTarazTable ? $t('employer.trazAnalysis.hide_table') : $t('employer.trazAnalysis.show_table') }}
                        </button>
                        
                        <!-- Taraz Table (Collapsible) -->
                        <div v-if="showTarazTable" class="taraz-table-container">
                            <table class="taraz-table">
                                <thead>
                                    <tr>
                                        <th>{{ $t('employer.trazAnalysis.table_code') }}</th>
                                        <th>{{ $t('employer.trazAnalysis.table_name') }}</th>
                                        <th>{{ $t('employer.trazAnalysis.table_opening_debit') }}</th>
                                        <th>{{ $t('employer.trazAnalysis.table_opening_credit') }}</th>
                                        <th>{{ $t('employer.trazAnalysis.table_period_debit') }}</th>
                                        <th>{{ $t('employer.trazAnalysis.table_period_credit') }}</th>
                                        <th>{{ $t('employer.trazAnalysis.table_closing_debit') }}</th>
                                        <th>{{ $t('employer.trazAnalysis.table_closing_credit') }}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-for="account in tarazData.slice(0, 10)" :key="account.HandCode">
                                        <td>{{ account.HandCode }}</td>
                                        <td>{{ account.HandTitle }}</td>
                                        <td class="debit">{{ formatCurrency(account.MandeFirst_Bed) }}</td>
                                        <td class="credit">{{ formatCurrency(account.MandeFirst_Bes) }}</td>
                                        <td class="debit">{{ formatCurrency(account.Sum_Bed) }}</td>
                                        <td class="credit">{{ formatCurrency(account.Sum_Bes) }}</td>
                                        <td class="debit">{{ formatCurrency(account.MandeLast_Bed) }}</td>
                                        <td class="credit">{{ formatCurrency(account.MandeLast_Bes) }}</td>
                                    </tr>
                                </tbody>
                            </table>
                            <div class="table-footer">
                                <span>{{ $t('employer.trazAnalysis.table_footer', { count: tarazData.length }) }}</span>
                            </div>
                        </div>
                    </div>
                </div>
                
                <!-- AI Analysis Results -->
                <div v-if="analyses.length > 0" class="analyses-container" style="direction: rtl;">
                    <div class="analyses-header">
                        <h3>🧠 {{ $t('employer.trazAnalysis.results_title') }}</h3>
                        <div class="analyses-stats">
                            <span class="stat-badge">{{ analyses.length }} {{ $t('employer.trazAnalysis.analyses_count') }}</span>
                            <span class="stat-badge">{{ analysisLanguageText }} {{ $t('employer.trazAnalysis.language') }}</span>
                            <span class="stat-badge">{{ analysisTypeText }} {{ $t('employer.trazAnalysis.type') }}</span>
                        </div>
                    </div>
                    
                    <!-- Analysis Tabs -->
                    <div class="analysis-tabs">
                        <button 
                            v-for="(analysis, index) in analyses" 
                            :key="index"
                            @click="activeAnalysisTab = index"
                            :class="['tab-btn', activeAnalysisTab === index ? 'active' : '', analysis.is_synthesized ? 'synthesized-tab' : '']"
                        >
                            <span v-if="analysis.is_synthesized" class="synthesized-badge">🌟</span>
                            <span class="provider-icon">{{ getProviderIcon(analysis.provider) }}</span>
                            {{ analysis.model }}
                            <span v-if="analysis.is_synthesized" class="synthesized-label">{{ $t('employer.trazAnalysis.composite') }}</span>
                            <span v-else class="latency-badge">{{ analysis.latency_ms }}ms</span>
                        </button>
                    </div>

                    <!-- Analysis Content -->
                    <div class="analysis-content">
                        <div v-for="(analysis, index) in analyses" 
                            :key="index"
                            v-show="activeAnalysisTab === index"
                            :class="['analysis-card', analysis.is_synthesized ? 'synthesized-card' : '']"
                        >
                            <div class="analysis-header">
                                <div class="analysis-meta">
                                    <span v-if="analysis.is_synthesized" class="synthesized-badge-large">🌟 {{ $t('employer.trazAnalysis.comprehensive_analysis') }}</span>
                                    <span v-else class="provider-badge">{{ analysis.provider }}</span>
                                    
                                    <span v-if="!analysis.is_synthesized">
                                        <span class="model-badge">{{ analysis.model }}</span>
                                        <span class="latency">{{ analysis.latency_ms }} ms</span>
                                        <span class="pricing-tier">{{ analysis.pricing_tier === 'free' ? $t('employer.trazAnalysis.free') : $t('employer.trazAnalysis.paid') }}</span>
                                    </span>
                                </div>
                                <div class="analysis-actions">
                                    <button @click="copyToClipboard(analysis.content)" class="action-btn">
                                        📋 {{ $t('employer.trazAnalysis.copy') }}
                                    </button>
                                </div>
                            </div>
                            
                            <div class="analysis-body">
                                <div v-if="analysis.error" class="analysis-error">
                                    <div class="error-alert">⚠️ {{ analysis.error }}</div>
                                </div>
                                <div v-else class="analysis-text">
                                    <div v-if="targetLanguage === 'fa'" dir="rtl" class="persian-text">
                                        <div v-if="analysis.is_synthesized" class="synthesized-intro">
                                            <p><strong>📊 {{ $t('employer.trazAnalysis.composite_intro') }}</strong></p>
                                            <p class="synthesized-note">{{ $t('employer.trazAnalysis.composite_note') }}</p>
                                        </div>
                                        <div class="formatted-analysis" v-html="formatAnalysisContentEnhanced(analysis.content)"></div>
                                    </div>
                                    <div v-else dir="ltr" class="english-text">
                                        <div v-if="analysis.is_synthesized" class="synthesized-intro">
                                            <p><strong>📊 {{ $t('employer.trazAnalysis.composite_intro_en') }}</strong></p>
                                            <p class="synthesized-note">{{ $t('employer.trazAnalysis.composite_note_en') }}</p>
                                        </div>
                                        <div class="formatted-analysis" v-html="formatAnalysisContentEnhanced(analysis.content)"></div>
                                    </div>
                                </div>
                            </div>
                            
                            <div class="analysis-footer">
                                <div class="quality-rating">
                                    <span>{{ $t('employer.trazAnalysis.quality') }}:</span>
                                    <div class="stars">
                                        <span 
                                            v-for="star in 5" 
                                            :key="star"
                                            :class="['star', star <= calculateQualityScore(analysis) ? 'filled' : '']"
                                            @click="rateAnalysis(analysis, star)"
                                        >
                                            ★
                                        </span>
                                    </div>
                                    <span v-if="analysis.is_synthesized" class="synthesized-quality">({{ $t('employer.trazAnalysis.optimized') }})</span>
                                </div>
                            </div>
                        </div>
                    </div>
                    
                    <!-- Comparative Analysis -->
                    <div v-if="analyses.length > 1" class="comparative-analysis">
                        <h4>📊 {{ $t('employer.trazAnalysis.comparative_title') }}</h4>
                        <div class="comparison-grid">
                            <div class="comparison-item">
                                <span class="comparison-label">{{ $t('employer.trazAnalysis.avg_length') }}:</span>
                                <span class="comparison-value">{{ averageAnalysisLength }} {{ $t('employer.trazAnalysis.words') }}</span>
                            </div>
                            <div class="comparison-item">
                                <span class="comparison-label">{{ $t('employer.trazAnalysis.fastest') }}:</span>
                                <span class="comparison-value">{{ fastestAnalysis.model }} ({{ fastestAnalysis.latency }}ms)</span>
                            </div>
                            <div class="comparison-item">
                                <span class="comparison-label">{{ $t('employer.trazAnalysis.longest') }}:</span>
                                <span class="comparison-value">{{ longestAnalysis.model }} ({{ longestAnalysis.length }} {{ $t('employer.trazAnalysis.words') }})</span>
                            </div>
                        </div>
                    </div>
                </div>
                
                <!-- No Analysis State -->
                <div v-if="!loadingAnalysis && !loadingTaraz && analyses.length === 0 && tarazData.length === 0" 
                     class="empty-state">
                    <div class="empty-icon">🤖</div>
                    <h4 style="color: var(--text);">{{ $t('employer.trazAnalysis.empty_title') }}</h4>
                    <p style="color: var(--text);">{{ $t('employer.trazAnalysis.empty_description') }}</p>
                    <p class="empty-tip">💡 {{ $t('employer.trazAnalysis.empty_tip') }}</p>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { ref, computed, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import Navbar from '@/components/Navbar.vue'
import { employerService, accountingService } from '@/services/api'
import moment from 'moment-jalaali'

export default {
    name: 'TarazAnalysis',
    components: {
        Navbar
    },
    setup() {
        const router = useRouter()
        
        // State
        const loadingAnalysis = ref(false)
        const loadingTaraz = ref(false)
        const loadingArz = ref(false)
        const error = ref(null)
        
        // Data
        const tarazData = ref([])
        const analyses = ref([])
        const arzList = ref([])
        
        // Settings
        const selectedArz = ref(1)
        const selectedMonth = ref('')
        const selectedYear = ref(new Date().getFullYear() - 621) // Convert to Persian year
        const analysisType = ref('comprehensive')
        const targetLanguage = ref('fa')
        const userQuery = ref('')
        const showTarazTable = ref(false)
        const activeAnalysisTab = ref(0)
        const selectedCmLevel = ref(1)

        // Add these to your component setup
        const loadingSteps = ref([
            'در حال پردازش ترازهای ورودی',
            'در حال آماده‌سازی داده‌های مالی',
            'در حال اتصال به مدل‌های هوش مصنوعی',
            'در حال دریافت پاسخ‌های هوش مصنوعی',
            'در حال تحلیل پاسخ‌های دریافتی',
            'در حال ترکیب و آنالیز پاسخ‌ها',
            'در حال تولید آنالیز نهایی'
        ])

        const currentLoadingStep = ref('')
        const stepIndex = ref(0)
        const progressPercentage = ref(0)
        const stepInterval = ref(null)
        const estimatedTime = ref(25) // Initial estimate in seconds
        const activeModels = ref(0)
        const remainingTime = ref(estimatedTime.value)

        // Loading tips (randomized)
        const loadingTips = ref([
            'تحلیل‌های جامع معمولاً ۲۰-۳۰ ثانیه زمان نیاز دارند',
            'در حال استفاده از ۳-۵ مدل هوش مصنوعی مختلف',
            'تحلیل ترکیبی، بهترین یافته‌های همه مدل‌ها را در بر می‌گیرد',
            'نتایج به زبان فارسی بهینه شده‌اند',
            'سیستم در حال ترکیب پاسخ‌های چندین تحلیلگر AI است',
            'کیفیت تحلیل به تعداد داده‌های ورودی بستگی دارد',
            'نتایج نهایی شامل توصیه‌های عملی می‌شوند'
        ])

        const randomLoadingTip = ref('')

        // Methods for loading animation
        const startLoadingAnimation = () => {
            // Reset values
            stepIndex.value = 0
            progressPercentage.value = 0
            currentLoadingStep.value = loadingSteps.value[0]
            remainingTime.value = estimatedTime.value
            
            // Randomize number of AI models (3-6)
            activeModels.value = 3 + Math.floor(Math.random() * 4)
            
            // Set random tip
            randomLoadingTip.value = loadingTips.value[Math.floor(Math.random() * loadingTips.value.length)]
            
            // Start step progression
            stepInterval.value = setInterval(() => {
                if (stepIndex.value < loadingSteps.value.length - 1) {
                    stepIndex.value++
                    currentLoadingStep.value = loadingSteps.value[stepIndex.value]
                    
                    // Update progress percentage
                    progressPercentage.value = ((stepIndex.value + 1) / loadingSteps.value.length) * 100
                    
                    // Update estimated time (decrease as we progress)
                    const timeReduction = Math.floor(Math.random() * 3) + 2
                    remainingTime.value = Math.max(3, remainingTime.value - timeReduction)
                    estimatedTime.value = remainingTime.value
                }
            }, 3000 + Math.random() * 2000) // Random interval between 3-5 seconds
        }

        const stopLoadingAnimation = () => {
            if (stepInterval.value) {
                clearInterval(stepInterval.value)
                stepInterval.value = null
            }
            
            // Complete the progress
            progressPercentage.value = 100
            currentLoadingStep.value = 'آماده‌سازی نتایج...'
            estimatedTime.value = 0
        }

        const scrollToLoading = () => {
            // Use setTimeout to ensure DOM is updated
            setTimeout(() => {
                const loadingElement = document.querySelector('.loading-state')
                if (loadingElement) {
                    loadingElement.scrollIntoView({ 
                        behavior: 'smooth', 
                        block: 'center' 
                    })
                    
                    // Add visual highlight effect
                    loadingElement.style.boxShadow = '0 0 30px rgba(52, 152, 219, 0.5)'
                    setTimeout(() => {
                        loadingElement.style.boxShadow = ''
                    }, 1000)
                }
            }, 100)
        }
        
        // Persian months
        const persianMonths = [
            'فروردین', 'اردیبهشت', 'خرداد', 'تیر', 'مرداد', 'شهریور',
            'مهر', 'آبان', 'آذر', 'دی', 'بهمن', 'اسفند'
        ]
        
        // AI Models status (simplified)
        const availableModels = ref([
            { name: 'GitHub Cohere', status: 'active' },
            { name: 'GitHub Phi-4', status: 'active' },
            { name: 'OpenRouter Gemini', status: 'active' },
            { name: 'OpenRouter Claude', status: 'active' },
        ])
        
        // Computed properties
        const selectedMonthName = computed(() => {
            if (!selectedMonth.value) return ''
            return persianMonths[selectedMonth.value - 1]
        })

        const selectedCmLevelText = computed(() => {
            switch (selectedCmLevel.value) {
                case 1: return 'گروه'
                case 2: return 'کل'
                case 3: return 'معین'
                case 4: return 'تفضیلی'
                default: return 'گروه'
            }
        })
        
        const selectedArzName = computed(() => {
            const arz = arzList.value.find(a => a.idd === selectedArz.value)
            return arz ? arz.nam : 'ریال'
        })
        
        const analysisTypeText = computed(() => {
            const types = {
                'quick': 'سریع',
                'comprehensive': 'جامع',
                'technical': 'فنی',
                'detailed': 'تفصیلی'
            }
            return types[analysisType.value] || 'جامع'
        })
        
        const analysisLanguageText = computed(() => {
            return targetLanguage.value === 'fa' ? 'فارسی' : 'انگلیسی'
        })
        
        const calculatedTotals = computed(() => {
            let totalAssets = 0
            let totalLiabilities = 0
            let totalEquity = 0
            
            tarazData.value.forEach(account => {
                console.log(account)
                const accountCode = account.HandCode || ''
                const netBalance = (account.MandeLast_Bed || 0) - (account.MandeLast_Bes || 0)
                
                if (accountCode.startsWith('1')) {
                    totalAssets += netBalance
                } else if (accountCode.startsWith('2')) {
                    totalLiabilities += -netBalance
                } else if (accountCode.startsWith('3')) {
                    totalEquity += -netBalance
                }
            })
            
            return {
                totalAssets: Math.max(totalAssets, 0),
                totalLiabilities,
                totalEquity: Math.max(totalEquity, 0)
            }
        })
        
        const successfulAnalyses = computed(() => {
            return analyses.value.filter(a => !a.error && a.content).length
        })
        
        const averageLatency = computed(() => {
            if (analyses.value.length === 0) return 0
            const validAnalyses = analyses.value.filter(a => a.latency_ms)
            if (validAnalyses.length === 0) return 0
            const sum = validAnalyses.reduce((acc, a) => acc + a.latency_ms, 0)
            return Math.round(sum / validAnalyses.length)
        })
        
        const averageAnalysisLength = computed(() => {
            if (analyses.value.length === 0) return 0
            const validAnalyses = analyses.value.filter(a => a.content)
            if (validAnalyses.length === 0) return 0
            const totalWords = validAnalyses.reduce((acc, a) => {
                return acc + (a.content.split(/\s+/).length || 0)
            }, 0)
            return Math.round(totalWords / validAnalyses.length)
        })
        
        const fastestAnalysis = computed(() => {
            if (analyses.value.length === 0) return { model: '--', latency: 0 }
            const validAnalyses = analyses.value.filter(a => a.latency_ms)
            if (validAnalyses.length === 0) return { model: '--', latency: 0 }
            const fastest = validAnalyses.reduce((prev, current) => 
                prev.latency_ms < current.latency_ms ? prev : current
            )
            return { model: fastest.model, latency: fastest.latency_ms }
        })
        
        const longestAnalysis = computed(() => {
            if (analyses.value.length === 0) return { model: '--', length: 0 }
            const validAnalyses = analyses.value.filter(a => a.content)
            if (validAnalyses.length === 0) return { model: '--', length: 0 }
            const longest = validAnalyses.reduce((prev, current) => {
                const prevLength = prev.content.split(/\s+/).length
                const currentLength = current.content.split(/\s+/).length
                return prevLength > currentLength ? prev : current
            })
            return { model: longest.model, length: longest.content.split(/\s+/).length }
        })
        
        // LocalStorage Functions
        const saveAnalysisToLocalStorage = () => {
            try {
                const analysisData = {
                    timestamp: new Date().toISOString(),
                    selectedArz: selectedArz.value,
                    selectedMonth: selectedMonth.value,
                    selectedYear: selectedYear.value,
                    analysisTypes: analysisTypes.value,
                    analysisType: analysisType.value,
                    targetLanguage: targetLanguage.value,
                    userQuery: userQuery.value,
                    tarazData: tarazData.value,
                    analyses: analyses.value,
                    showTarazTable: showTarazTable.value,
                    activeAnalysisTab: activeAnalysisTab.value
                }
                
                localStorage.setItem('latestTarazAnalysis', JSON.stringify(analysisData))
                console.log('✅ Analysis saved to localStorage')
            } catch (error) {
                console.error('❌ Error saving to localStorage:', error)
            }
        }
        
        const loadAnalysisFromLocalStorage = () => {
            try {
                const savedData = localStorage.getItem('latestTarazAnalysis')
                if (!savedData) {
                    console.log('No saved analysis found in localStorage')
                    return false
                }
                
                const analysisData = JSON.parse(savedData)
                
                // Check if data is not too old (e.g., less than 7 days)
                const savedDate = new Date(analysisData.timestamp)
                const currentDate = new Date()
                const daysDiff = (currentDate - savedDate) / (1000 * 60 * 60 * 24)
                
                if (daysDiff > 7) {
                    console.log('Saved analysis is older than 7 days, ignoring')
                    localStorage.removeItem('latestTarazAnalysis')
                    return false
                }

                if (analysisData.analysisTypes) {
                    analysisTypes.value = analysisData.analysisTypes
                }
                
                // Load the data
                selectedArz.value = analysisData.selectedArz || 1
                selectedMonth.value = analysisData.selectedMonth || ''
                selectedYear.value = analysisData.selectedYear || (new Date().getFullYear() - 621)
                analysisType.value = analysisData.analysisType || 'comprehensive'
                targetLanguage.value = analysisData.targetLanguage || 'fa'
                userQuery.value = analysisData.userQuery || ''
                tarazData.value = analysisData.tarazData || []
                analyses.value = analysisData.analyses || []
                showTarazTable.value = analysisData.showTarazTable || false
                activeAnalysisTab.value = analysisData.activeAnalysisTab || 0
                
                console.log('✅ Analysis loaded from localStorage')
                console.log(`Loaded ${analyses.value.length} analyses and ${tarazData.value.length} taraz records`)
                
                return true
            } catch (error) {
                console.error('❌ Error loading from localStorage:', error)
                // Clear corrupted data
                localStorage.removeItem('latestTarazAnalysis')
                return false
            }
        }
        
        const clearSavedAnalysis = () => {
            try {
                localStorage.removeItem('latestTarazAnalysis')
                console.log('✅ Saved analysis cleared from localStorage')
                return true
            } catch (error) {
                console.error('❌ Error clearing localStorage:', error)
                return false
            }
        }
        
        // Save analysis whenever analyses or tarazData changes
        const saveAnalysisOnChange = () => {
            if (analyses.value.length > 0 || tarazData.value.length > 0) {
                saveAnalysisToLocalStorage()
            }
        }
        
        // Methods
        const navigateToDashboard = () => {
            router.push('/employer/dashboard')
        }
        
        const fetchAllArz = async () => {
            loadingArz.value = true
            try {
                const response = await accountingService.getAllArz()
                if (response.data.success) {
                    arzList.value = response.data.data
                    console.log('✅ ARZ list loaded:', arzList.value)
                }
            } catch (err) {
                console.error('❌ Error fetching ARZ:', err)
                error.value = 'خطا در بارگذاری لیست ارزها'
            } finally {
                loadingArz.value = false
            }
        }
        
        const onArzChange = () => {
            console.log('Selected ARZ changed to:', selectedArz.value)
            saveAnalysisOnChange()
        }
        
        const onMonthChange = () => {
            console.log('Selected month changed to:', selectedMonth.value)
            saveAnalysisOnChange()
        }
        
        const onYearChange = () => {
            console.log('Selected year changed to:', selectedYear.value)
            saveAnalysisOnChange()
        }
        
        const setLanguage = (lang) => {
            targetLanguage.value = lang
            saveAnalysisOnChange()
        }
        
        const setExampleQuery = (query) => {
            userQuery.value = query
        }
        
        const fetchTarazDataOnly = async () => {
            if (!selectedMonth.value || !selectedYear.value) {
                error.value = 'لطفاً ماه و سال را انتخاب کنید'
                return
            }
            
            loadingTaraz.value = true
            error.value = null
            
            try {
                // Calculate date range for the selected month/year
                const dateFrom = `${selectedYear.value}/${selectedMonth.value.toString().padStart(2, '0')}/01`
                const dateTo = `${selectedYear.value}/${selectedMonth.value.toString().padStart(2, '0')}/31`
                
                const requestData = {
                    arz: selectedArz.value,
                    cm: selectedCmLevel.value,
                    mah: selectedMonth.value,
                    date_from: dateFrom,
                    date_to: dateTo,
                    include_summary: true
                }
                
                console.log('Fetching taraz data:', requestData)
                
                const response = await employerService.getTarazData(requestData)
                
                if (response.data.success) {
                    tarazData.value = response.data.data
                    console.log(`✅ Taraz data loaded: ${tarazData.value.length} accounts`)
                    
                    // Reset analyses since we have new data
                    analyses.value = []
                    
                    // Show success message
                    error.value = null
                    
                    // Save to localStorage
                    saveAnalysisToLocalStorage()
                } else {
                    error.value = response.data.message || 'خطا در دریافت داده‌های تراز'
                }
            } catch (err) {
                console.error('❌ Error fetching taraz data:', err)
                error.value = err.response?.data?.detail || err.message || 'خطا در دریافت داده‌های تراز'
            } finally {
                loadingTaraz.value = false
            }
        }
        
        // In fetchDataAndAnalyze() method:
        const fetchDataAndAnalyze = async () => {
            if (!selectedMonth.value || !selectedYear.value) {
                error.value = 'لطفاً ماه و سال را انتخاب کنید'
                return
            }
            
            if (tarazData.value.length === 0) {
                // First fetch taraz data
                await fetchTarazDataOnly()
                if (tarazData.value.length === 0) {
                    error.value = 'داده‌ای برای تحلیل وجود ندارد'
                    return
                }
            }
            
            loadingAnalysis.value = true
            error.value = null

            scrollToLoading()
            startLoadingAnimation()
            
            try {
                // Calculate date range
                const dateFrom = `${selectedYear.value}/${selectedMonth.value.toString().padStart(2, '0')}/01`
                const dateTo = `${selectedYear.value}/${selectedMonth.value.toString().padStart(2, '0')}/31`
                
                // Prepare summary data from tarazData
                const summaryData = {
                    total_pre_debit: tarazData.value.reduce((sum, acc) => sum + (acc.MandeFirst_Bed || 0), 0),
                    total_pre_credit: tarazData.value.reduce((sum, acc) => sum + (acc.MandeFirst_Bes || 0), 0),
                    total_today_debit: tarazData.value.reduce((sum, acc) => sum + (acc.Sum_Bed || 0), 0),
                    total_today_credit: tarazData.value.reduce((sum, acc) => sum + (acc.Sum_Bes || 0), 0),
                    total_final_debit: tarazData.value.reduce((sum, acc) => sum + (acc.MandeLast_Bed || 0), 0),
                    total_final_credit: tarazData.value.reduce((sum, acc) => sum + (acc.MandeLast_Bes || 0), 0)
                }
                
                const requestData = {
                    // Taraz parameters
                    arz: selectedArz.value,
                    cm: selectedCmLevel.value,
                    mah: selectedMonth.value,
                    date_from: dateFrom,
                    date_to: dateTo,
                    include_summary: true,
                    
                    // Analysis parameters
                    analysis_type: selectedAnalysisTypes.value,
                    user_query: userQuery.value,
                    target_language: targetLanguage.value,
                    
                    // Pass taraz data directly (if we have it)
                    taraz_data: tarazData.value,
                    summary_data: summaryData
                }
                
                console.log('Sending analysis request:', requestData)
                
                const response = await employerService.getTarazWithAnalysis(requestData)
                
                if (response.data.success) {
                    // Create the synthesized analysis as the first item
                    if (response.data.synthesized_analysis) {
                        const synthesizedAnalysis = {
                            provider: "🤖 سیستم ترکیبی",
                            model: "تحلیل جامع ترکیبی",
                            content: response.data.synthesized_analysis,
                            latency_ms: 0, // You might want to calculate this differently
                            error: null,
                            pricing_tier: "free",
                            is_synthesized: true, // Add a flag to identify synthesized analysis
                            quality_score: 100 // Highest quality
                        }
                        
                        // Start with synthesized analysis, then add individual analyses
                        const allAnalyses = [synthesizedAnalysis];
                        
                        // Add individual analyses if available
                        if (response.data.individual_analyses && response.data.individual_analyses.length > 0) {
                            response.data.individual_analyses.forEach(analysis => {
                                allAnalyses.push({
                                    ...analysis,
                                    is_synthesized: false
                                });
                            });
                        }
                        
                        analyses.value = allAnalyses;
                    } else {
                        // Fallback: use individual analyses only
                        analyses.value = response.data.individual_analyses || [];
                    }
                    
                    console.log(`✅ AI analyses received: ${analyses.value.length} analyses (${response.data.individual_analyses?.length || 0} individual + 1 synthesized)`)
                    
                    // Set first analysis (synthesized) as active
                    if (analyses.value.length > 0) {
                        activeAnalysisTab.value = 0
                    }
                    
                    // Store taraz data if returned
                    if (response.data.taraz_data && response.data.taraz_data.length > 0) {
                        tarazData.value = response.data.taraz_data
                    }
                    
                    // Save to localStorage
                    saveAnalysisToLocalStorage()
                } else {
                    error.value = response.data.message || 'خطا در تحلیل داده‌ها'
                }
            } catch (err) {
                console.error('❌ Error in AI analysis:', err)
                error.value = err.response?.data?.detail || err.message || 'خطا در تحلیل هوش مصنوعی'
            } finally {
                loadingAnalysis.value = false
                stopLoadingAnimation()
            }
        }
        
        const getProviderIcon = (provider) => {
            const icons = {
                'GitHubAI': '🐙',
                'OpenRouter': '🌐',
                'Gemini': '💎',
                '🤖 سیستم ترکیبی': '🌟'
            }
            return icons[provider] || '🤖'
        }
        
        const copyToClipboard = async (text) => {
            try {
                await navigator.clipboard.writeText(text)
                alert('تحلیل در کلیپ‌بورد کپی شد')
            } catch (err) {
                console.error('Failed to copy:', err)
                alert('خطا در کپی کردن')
            }
        }
        
        const speakAnalysis = (text) => {
            if ('speechSynthesis' in window) {
                const speech = new SpeechSynthesisUtterance(text)
                speech.lang = targetLanguage.value === 'fa' ? 'fa-IR' : 'en-US'
                speech.rate = 0.9
                window.speechSynthesis.speak(speech)
            } else {
                alert('مرورگر شما از پخش صدا پشتیبانی نمی‌کند')
            }
        }
        
        const translateAnalysis = () => {
            // This would require a translation API
            alert('این قابلیت نیاز به API ترجمه دارد')
        }
        
        const calculateQualityScore = (analysis) => {
            // Simple quality score based on content length
            if (!analysis.content) return 0
            const length = analysis.content.length
            if (length < 100) return 1
            if (length < 300) return 2
            if (length < 600) return 3
            if (length < 1000) return 4
            return 5
        }
        
        const rateAnalysis = (analysis, stars) => {
            console.log(`Rated analysis ${analysis.model} with ${stars} stars`)
            // In a real app, you'd send this to your backend
            alert(`امتیاز ${stars} ستاره برای تحلیل ثبت شد`)
        }
        
        const exportAnalysis = (analysis) => {
            const content = `
                تحلیل ترازنامه - ${analysis.model}
                تاریخ: ${selectedMonthName.value} ${selectedYear.value}
                ارز: ${selectedArzName.value}
                نوع تحلیل: ${analysisTypeText.value}
                
                ${analysis.content}
            `
            
            const blob = new Blob([content], { type: 'text/plain' })
            const link = document.createElement('a')
            link.href = URL.createObjectURL(blob)
            link.download = `تحلیل-تراز-${selectedYear.value}-${selectedMonth.value}-${analysis.model}.txt`
            link.click()
        }
        
        const clearError = () => {
            error.value = null
        }
        
        const formatCurrency = (amount) => {
            if (!amount && amount !== 0) return '۰'
            const arz = arzList.value.find(a => a.idd === selectedArz.value)
            const currencySign = arz ? arz.sign : 'ریال'
            return new Intl.NumberFormat('fa-IR').format(Math.round(amount)) + ' ' + currencySign
        }
        
        const formatAnalysisContent = (content) => {
            if (!content) return '';
            
            let formatted = content;
            
            // 1. Fix newlines - replace \n with actual line breaks
            formatted = formatted.replace(/\\n/g, '\n');
            
            // 2. Handle bold text **text** → <strong>text</strong>
            formatted = formatted.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
            
            // 3. Handle headings (like **Heading** followed by \n\n)
            formatted = formatted.replace(/\*\*(.*?)\*\*\s*\n\s*\n/g, '<h4 class="analysis-heading">$1</h4>\n');
            
            // 4. Handle bullet points with Persian dash
            formatted = formatted.replace(/\n\s*[-•*]\s*(.*?)(?=\n|$)/g, '\n<span class="bullet-point">•</span> $1');
            
            // 5. Handle numbered lists
            formatted = formatted.replace(/\n\s*(\d+)\.\s*(.*?)(?=\n|$)/g, '\n<span class="list-number">$1.</span> $2');
            
            // 6. Format numbers with commas (Persian numbers)
            formatted = formatted.replace(/(\d{1,3}(?:,\d{3})*(?:\.\d+)?)(?=\s*(?:ریال|تومان|میلیارد|میلیون|هزار)?\b)/g, (match) => {
                // Add number formatting
                return '<span class="number-highlight">' + match + '</span>';
            });
            
            // 7. Handle financial ratios and formulas
            formatted = formatted.replace(/(نسبت جاری|نسبت بدهی|نسبت دارایی|نسبت.*?):\s*(.*?)(?=\n|$)/g, 
                '<div class="financial-ratio"><span class="ratio-label">$1:</span> <span class="ratio-value">$2</span></div>');
            
            // 8. Handle formulas and calculations - Fixed escaping
            formatted = formatted.replace(/\\\[/g, '[').replace(/\\\]/g, ']');
            formatted = formatted.replace(/\[(.*?)\]/g, '<span class="formula">[$1]</span>');
            
            // 9. Split into paragraphs
            const paragraphs = formatted.split('\n\n').filter(p => p.trim());
            
            // Wrap each paragraph in appropriate HTML
            const formattedParagraphs = paragraphs.map(para => {
                if (para.includes('<h4')) {
                    return para;
                } else if (para.includes('<div class="financial-ratio"')) {
                    return para;
                } else if (para.includes('bullet-point') || para.includes('list-number')) {
                    return `<div class="analysis-list">${para}</div>`;
                } else if (para.trim().startsWith('-') || para.trim().match(/^\d+\./)) {
                    return `<div class="analysis-list">${para}</div>`;
                } else {
                    return `<p class="analysis-paragraph">${para}</p>`;
                }
            });
            
            return formattedParagraphs.join('\n');
        };

        const formatAnalysisContentEnhanced = (content) => {
            if (!content) return '';
            
            // Clean up the content first
            let formatted = content.trim();
            
            // Fix common issues
            formatted = formatted.replace(/\\\*/g, '*');
            formatted = formatted.replace(/\\n/g, '\n');
            formatted = formatted.replace(/\\/g, '');
            
            // Process in sections
            const lines = formatted.split('\n');
            const processedLines = [];
            
            let inList = false;
            let inNumberedList = false;
            
            for (let i = 0; i < lines.length; i++) {
                let line = lines[i].trim();
                
                if (!line) {
                    if (inList || inNumberedList) {
                        processedLines.push('</ul>');
                        inList = false;
                        inNumberedList = false;
                    }
                    continue;
                }
                
                // Check for headers (lines with ** at start and end)
                if (line.startsWith('**') && line.endsWith('**')) {
                    const headerText = line.replace(/\*\*/g, '');
                    processedLines.push(`<h4 class="analysis-section-header">${headerText}</h4>`);
                    continue;
                }
                
                // Check for bullet points
                if (line.match(/^[-•*]\s+/)) {
                    if (!inList) {
                        processedLines.push('<ul class="analysis-bullet-list">');
                        inList = true;
                    }
                    const itemText = line.replace(/^[-•*]\s+/, '');
                    processedLines.push(`<li class="bullet-item">${formatInlineText(itemText)}</li>`);
                    continue;
                }
                
                // Check for numbered lists
                if (line.match(/^\d+\.\s+/)) {
                    if (!inNumberedList) {
                        processedLines.push('<ol class="analysis-numbered-list">');
                        inNumberedList = true;
                    }
                    const itemText = line.replace(/^\d+\.\s+/, '');
                    processedLines.push(`<li class="numbered-item">${formatInlineText(itemText)}</li>`);
                    continue;
                }
                
                // End lists if we're in one and this line isn't a list item
                if ((inList || inNumberedList) && !line.match(/^[-•*\d]/)) {
                    processedLines.push(inList ? '</ul>' : '</ol>');
                    inList = false;
                    inNumberedList = false;
                }
                
                // Process regular paragraphs
                if (line) {
                    // Check if this is a paragraph that should be emphasized
                    if (line.includes('نتیجه:') || line.includes('توصیه:') || line.includes('توجه:')) {
                        processedLines.push(`<p class="analysis-highlight-paragraph">${formatInlineText(line)}</p>`);
                    } else {
                        processedLines.push(`<p class="analysis-paragraph">${formatInlineText(line)}</p>`);
                    }
                }
            }
            
            // Close any open lists
            if (inList) processedLines.push('</ul>');
            if (inNumberedList) processedLines.push('</ol>');
            
            return processedLines.join('\n');
        };

        // Helper function to format inline text
        const formatInlineText = (text) => {
            let formatted = text;
            
            // Bold text
            formatted = formatted.replace(/\*\*(.*?)\*\*/g, '<strong class="bold-text">$1</strong>');
            
            // Numbers with commas (highlight financial figures)
            formatted = formatted.replace(/(\d[\d,]*\.?\d*)(?=\s*(?:ریال|تومان|میلیارد|میلیون|هزار|بلیون)?)/g, 
                '<span class="financial-number">$1</span>');
            
            // Ratios and percentages
            formatted = formatted.replace(/(نسبت|درصد|میانگین|میانه|حداکثر|حداقل|متوسط):?\s*([\d.,]+%?)/gi, 
                '<span class="ratio-term">$1</span>: <span class="ratio-value">$2</span>');
            
            // Formulas
            formatted = formatted.replace(/\[(.*?)\]/g, '<span class="formula-box">[$1]</span>');
            
            return formatted;
        };

        // Add this to your setup() function
        const analysisTypes = ref([
        { id: 'Default', label: 'تحلیل پایه', checked: true },
        { id: 'ARIMA', label: 'تحلیل پیش‌بینی', checked: false },
        { id: 'Regression', label: 'تحلیل رگرسیون', checked: false },
        { id: 'Statistical', label: 'کنترل آماری', checked: false },
        { id: 'Budget', label: 'انحراف بودجه', checked: false },
        { id: 'Sharpe Ratio', label: 'کارایی سرمایه‌گذاری', checked: false },
        { id: 'Clustering', label: 'خوشه‌بندی مشتریان', checked: false },
        { id: 'Discriminant', label: 'تحلیل تشخیصی', checked: false },
        ])

        // Computed property to get selected analysis types as string
        const selectedAnalysisTypes = computed(() => {
        return analysisTypes.value
            .filter(type => type.checked)
            .map(type => type.id)
            .join(' ')
        })

        // Method to handle analysis type checkbox changes
        const toggleAnalysisType = (typeId) => {
        const type = analysisTypes.value.find(t => t.id === typeId)
        if (type) {
            type.checked = !type.checked
            saveAnalysisOnChange()
        }
        }

        // Method to select/deselect all
        const selectAllAnalysisTypes = () => {
        analysisTypes.value.forEach(type => {
            type.checked = true
        })
        saveAnalysisOnChange()
        }

        const deselectAllAnalysisTypes = () => {
        analysisTypes.value.forEach(type => {
            type.checked = false
        })
        saveAnalysisOnChange()
        }

        const selectedAnalysisTypesCount = computed(() => {
            return analysisTypes.value.filter(type => type.checked).length
        })

        // Initialize
        onMounted(async () => {
            await fetchAllArz()
            
            // Try to load saved analysis first
            const hasSavedAnalysis = loadAnalysisFromLocalStorage()
            
            if (!hasSavedAnalysis) {
                // Set current Persian month by default
                const currentPersianMonth = moment().jMonth() + 1 // 1-12
                selectedMonth.value = currentPersianMonth
                selectedYear.value = moment().jYear()
                console.log(`Set default month to: ${persianMonths[currentPersianMonth - 1]} (${currentPersianMonth})`)
                console.log(`Set default year to: ${selectedYear.value}`)
            } else {
                console.log('Loaded saved analysis from localStorage')
            }
        })
        
        return {
            // State
            loadingAnalysis,
            loadingTaraz,
            loadingArz,
            error,
            tarazData,
            analyses,
            arzList,
            selectedArz,
            selectedMonth,
            selectedYear,
            analysisType,
            targetLanguage,
            userQuery,
            showTarazTable,
            activeAnalysisTab,
            
            // Constants
            persianMonths,
            availableModels,
            
            // Computed
            selectedMonthName,
            selectedArzName,
            analysisTypeText,
            analysisLanguageText,
            calculatedTotals,
            successfulAnalyses,
            averageLatency,
            averageAnalysisLength,
            fastestAnalysis,
            longestAnalysis,
            selectedCmLevel,
            selectedCmLevelText,

            estimatedTime,
            progressPercentage,
            currentLoadingStep,
            randomLoadingTip,

            analysisTypes,
            selectedAnalysisTypes,
            selectedAnalysisTypesCount,
            toggleAnalysisType,
            selectAllAnalysisTypes,
            deselectAllAnalysisTypes,
            
            // Methods
            navigateToDashboard,
            onArzChange,
            onMonthChange,
            onYearChange,
            setLanguage,
            setExampleQuery,
            fetchTarazDataOnly,
            fetchDataAndAnalyze,
            getProviderIcon,
            copyToClipboard,
            speakAnalysis,
            translateAnalysis,
            calculateQualityScore,
            rateAnalysis,
            exportAnalysis,
            clearError,
            formatCurrency,
            clearSavedAnalysis,
            formatAnalysisContent,
            formatAnalysisContentEnhanced
        }
    }
}
</script>

<style scoped>
.taraz-analysis-app {
    min-height: 100vh;
    background: var(--bg);
    padding: 15px;
    overflow-x: hidden;
}

.section-header {
    text-align: center;
    margin-bottom: 20px;
    padding: 15px;
    background: var(--card-bg);
    border-radius: 12px;
    box-shadow: 0 3px 10px var(--shadow-1);
    position: relative;
    border: 1px solid var(--card-border);
}

.section-title {
    color: var(--heading);
    font-size: 22px;
    font-weight: 700;
    margin-bottom: 5px;
    word-break: break-word;
}

.section-subtitle {
    color: var(--subtext);
    font-size: 14px;
    margin: 0;
    line-height: 1.4;
}

.back-arrow {
    position: absolute;
    right: 15px;
    top: 15px;
    background: var(--primary);
    color: white;
    border: none;
    border-radius: 50%;
    width: 36px;
    height: 36px;
    font-size: 18px;
    cursor: pointer;
    transition: all 0.3s ease;
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 10;
}

.back-arrow:hover {
    background: var(--primary-hover);
    transform: scale(1.1);
}

/* Main Content - Mobile First */
.main-content {
    display: flex;
    flex-direction: column;
    gap: 20px;
    margin-top: 15px;
    width: 100%;
    max-width: 100%;
    overflow: hidden;
    padding-bottom: 45PX;
}

/* Left Panel */
.left-panel {
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: 15px;
}

.control-card,
.models-card {
    background: var(--card-bg);
    border-radius: 12px;
    padding: 15px;
    box-shadow: 0 3px 10px var(--shadow-1);
    width: 100%;
    max-width: 100%;
    box-sizing: border-box;
    border: 1px solid var(--card-border);
}

.card-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 15px;
    padding-bottom: 12px;
    border-bottom: 1px solid var(--card-border);
    flex-wrap: wrap;
    gap: 10px;
}

.card-header h4 {
    color: var(--heading);
    font-size: 16px;
    margin: 0;
    flex: 1;
    min-width: 0;
}

.form-group {
    margin-bottom: 15px;
    width: 100%;
}

.form-label {
    display: block;
    margin-bottom: 6px;
    color: var(--heading);
    font-weight: 600;
    font-size: 13px;
}

.form-select,
.form-input,
.form-textarea {
    width: 100%;
    padding: 10px 12px;
    border: 1px solid var(--card-border);
    border-radius: 8px;
    font-size: 14px;
    transition: all 0.3s ease;
    background: var(--bg);
    box-sizing: border-box;
    max-width: 100%;
    color: var(--text);
}

.form-select:focus,
.form-input:focus,
.form-textarea:focus {
    outline: none;
    border-color: var(--primary);
    box-shadow: 0 0 0 2px var(--ring);
}

.form-textarea {
    resize: vertical;
    min-height: 70px;
    max-width: 100%;
}

.language-buttons {
    display: flex;
    gap: 8px;
    width: 100%;
}

.lang-btn {
    flex: 1;
    padding: 8px;
    border: 1px solid var(--card-border);
    background: var(--bg);
    border-radius: 8px;
    cursor: pointer;
    transition: all 0.3s ease;
    font-size: 13px;
    min-width: 0;
    word-break: keep-all;
    color: var(--text);
}

.lang-btn.active {
    background: var(--primary);
    color: white;
    border-color: var(--primary);
}

.query-examples {
    margin-top: 8px;
}

.example-tags {
    display: flex;
    gap: 6px;
    margin-top: 5px;
    flex-wrap: wrap;
}

.example-tags span {
    background: rgba(59, 130, 246, 0.1);
    color: var(--primary);
    padding: 4px 8px;
    border-radius: 16px;
    font-size: 11px;
    cursor: pointer;
    transition: all 0.3s ease;
    flex-shrink: 0;
}

.example-tags span:hover {
    background: rgba(59, 130, 246, 0.2);
}

.action-buttons {
    display: flex;
    flex-direction: column;
    gap: 10px;
    margin-top: 20px;
    width: 100%;
}

.btn-analyze,
.btn-fetch-data {
    padding: 12px;
    border: none;
    border-radius: 8px;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.3s ease;
    text-align: center;
    width: 100%;
    box-sizing: border-box;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.btn-analyze {
    background: linear-gradient(135deg, var(--gradient-1) 0%, var(--gradient-2) 100%);
    color: white;
}

.btn-analyze:hover:not(:disabled) {
    transform: translateY(-1px);
    box-shadow: 0 5px 10px var(--shadow-2);
}

.btn-analyze:disabled {
    opacity: 0.6;
    cursor: not-allowed;
}

.btn-fetch-data {
    background: var(--bg);
    color: var(--text);
    border: 1px solid var(--card-border);
}

.btn-fetch-data:hover:not(:disabled) {
    background: var(--hover);
    border-color: var(--primary);
}

/* Models Status */
.models-status {
    display: flex;
    flex-direction: column;
    gap: 10px;
    width: 100%;
}

.model-item {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 10px;
    background: var(--bg);
    border-radius: 8px;
    width: 100%;
    box-sizing: border-box;
}

.model-icon {
    font-size: 18px;
    flex-shrink: 0;
}

.model-info {
    flex: 1;
    display: flex;
    justify-content: space-between;
    align-items: center;
    min-width: 0;
}

.model-name {
    font-weight: 600;
    color: var(--text);
    font-size: 13px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    flex: 1;
}

.model-status {
    padding: 3px 8px;
    border-radius: 16px;
    font-size: 11px;
    font-weight: 600;
    flex-shrink: 0;
}

.model-status.active {
    background: rgba(16, 185, 129, 0.2);
    color: var(--green);
}

.model-status.inactive {
    background: rgba(249, 115, 22, 0.2);
    color: var(--orange-1);
}

.performance-stats {
    margin-top: 15px;
    padding-top: 12px;
    border-top: 1px solid var(--card-border);
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 10px;
    width: 100%;
}

.stat-item {
    display: flex;
    flex-direction: column;
    min-width: 0;
}

.stat-label {
    font-size: 11px;
    color: var(--subtext);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.stat-value {
    font-size: 12px;
    font-weight: 600;
    color: var(--text);
    overflow: hidden;
    text-overflow: ellipsis;
}

/* Right Panel */
.right-panel {
    display: flex;
    flex-direction: column;
    gap: 15px;
    width: 100%;
    max-width: 100%;
    overflow: hidden;
}

.loading-state,
.error-state,
.empty-state {
    background: var(--card-bg);
    border-radius: 12px;
    padding: 30px 20px;
    text-align: center;
    box-shadow: 0 3px 10px var(--shadow-1);
    width: 100%;
    box-sizing: border-box;
    border: 1px solid var(--card-border);
}

.loading-spinner-large {
    width: 50px;
    height: 50px;
    border: 4px solid var(--card-border);
    border-top: 4px solid var(--primary);
    border-radius: 50%;
    animation: spin 1s linear infinite;
    margin: 0 auto 15px;
}

@keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
}

.loading-spinner-small {
    display: inline-block;
    width: 14px;
    height: 14px;
    border: 2px solid var(--card-border);
    border-top: 2px solid var(--primary);
    border-radius: 50%;
    animation: spin 1s linear infinite;
    margin-right: 6px;
}

.error-icon {
    font-size: 40px;
    margin-bottom: 15px;
}

.btn-retry {
    margin-top: 15px;
    padding: 8px 20px;
    background: var(--orange-1);
    color: white;
    border: none;
    border-radius: 8px;
    cursor: pointer;
    font-size: 14px;
}

.empty-icon {
    font-size: 40px;
    margin-bottom: 15px;
    color: var(--subtext);
}

.empty-tip {
    color: var(--subtext);
    font-size: 13px;
    margin-top: 8px;
    line-height: 1.4;
}

/* Summary Card */
.summary-card {
    background: var(--card-bg);
    border-radius: 12px;
    padding: 15px;
    box-shadow: 0 3px 10px var(--shadow-1);
    width: 100%;
    max-width: 100%;
    box-sizing: border-box;
    overflow: hidden;
    border: 1px solid var(--card-border);
}

.summary-grid {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 10px;
    margin-bottom: 15px;
    width: 100%;
}

.summary-item {
    padding: 10px;
    background: var(--bg);
    border-radius: 8px;
    display: flex;
    flex-direction: column;
    gap: 4px;
    min-width: 0;
    word-break: break-word;
}

.summary-label {
    font-size: 11px;
    color: var(--subtext);
    line-height: 1.2;
    overflow: hidden;
    text-overflow: ellipsis;
}

.summary-value {
    font-weight: 600;
    color: var(--text);
    font-size: 12px;
    line-height: 1.3;
    overflow: hidden;
    text-overflow: ellipsis;
}

.summary-value.debit {
    color: var(--orange-1);
}

.summary-value.credit {
    color: var(--green);
}

.btn-toggle-table {
    width: 100%;
    padding: 10px;
    background: var(--bg);
    border: 1px solid var(--card-border);
    border-radius: 8px;
    cursor: pointer;
    font-size: 13px;
    margin-bottom: 12px;
    box-sizing: border-box;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    color: var(--text);
}

.taraz-table-container {
    overflow-x: auto;
    width: 100%;
    -webkit-overflow-scrolling: touch;
    border-radius: 8px;
    border: 1px solid var(--card-border);
}

.taraz-table {
    width: auto;
    min-width: 100%;
    border-collapse: collapse;
    font-size: 12px;
    table-layout: auto;
}

.taraz-table th {
    color: var(--text);
    padding: 8px 6px;
    text-align: right;
    font-weight: 600;
    font-size: 11px;
    white-space: nowrap;
    position: sticky;
    top: 0;
}

.taraz-table td {
    padding: 8px 6px;
    border-bottom: 1px solid var(--card-border);
    text-align: right;
    font-size: 11px;
    white-space: nowrap;
    color: var(--text);
}

.taraz-table tr:hover {
    background: var(--bg);
}

.taraz-table .debit {
    color: var(--orange-1);
    font-weight: 600;
}

.taraz-table .credit {
    color: var(--green);
    font-weight: 600;
}

.table-footer {
    text-align: center;
    padding: 8px;
    color: var(--subtext);
    font-size: 11px;
    background: var(--bg);
    border-top: 1px solid var(--card-border);
}

/* Analyses Container */
.analyses-container {
    background: var(--card-bg);
    border-radius: 12px;
    padding: 15px;
    box-shadow: 0 3px 10px var(--shadow-1);
    width: 100%;
    max-width: 100%;
    box-sizing: border-box;
    overflow: hidden;
    border: 1px solid var(--card-border);
}

.analyses-header {
    display: flex;
    flex-direction: column;
    gap: 10px;
    margin-bottom: 15px;
    padding-bottom: 12px;
    border-bottom: 1px solid var(--card-border);
    width: 100%;
}

.analyses-header h3 {
    color: var(--heading);
    margin: 0;
    font-size: 18px;
    line-height: 1.3;
    word-break: break-word;
}

.analyses-stats {
    display: flex;
    gap: 8px;
    flex-wrap: wrap;
}

.stat-badge {
    background: rgba(59, 130, 246, 0.1);
    color: var(--primary);
    padding: 4px 8px;
    border-radius: 16px;
    font-size: 11px;
    font-weight: 600;
    white-space: nowrap;
    flex-shrink: 0;
}

.analysis-tabs {
    display: flex;
    gap: 8px;
    margin-bottom: 15px;
    overflow-x: auto;
    padding-bottom: 8px;
    width: 100%;
    -webkit-overflow-scrolling: touch;
    scrollbar-width: thin;
}

.analysis-tabs::-webkit-scrollbar {
    height: 4px;
}

.analysis-tabs::-webkit-scrollbar-thumb {
    background: var(--muted-2);
    border-radius: 2px;
}

.tab-btn {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 8px 12px;
    background: var(--bg);
    border: 1px solid var(--card-border);
    border-radius: 8px;
    cursor: pointer;
    white-space: nowrap;
    transition: all 0.3s ease;
    flex-shrink: 0;
    font-size: 12px;
    min-height: 36px;
    color: var(--text);
}

.tab-btn.active {
    background: var(--primary);
    color: white;
    border-color: var(--primary);
}

.provider-icon {
    font-size: 14px;
    flex-shrink: 0;
}

.latency-badge {
    background: rgba(255, 255, 255, 0.2);
    padding: 2px 6px;
    border-radius: 10px;
    font-size: 10px;
    flex-shrink: 0;
}

.analysis-content {
    width: 100%;
    max-width: 100%;
    overflow: hidden;
}

.analysis-card {
    background: var(--bg);
    border-radius: 12px;
    padding: 15px;
    margin-bottom: 15px;
    width: 100%;
    box-sizing: border-box;
    overflow: hidden;
    border: 1px solid var(--card-border);
}

.analysis-header {
    display: flex;
    flex-direction: column;
    gap: 12px;
    margin-bottom: 15px;
    padding-bottom: 12px;
    border-bottom: 1px solid var(--card-border);
    width: 100%;
}

.analysis-meta {
    display: flex;
    gap: 8px;
    align-items: center;
    flex-wrap: wrap;
    width: 100%;
}

.provider-badge,
.model-badge {
    padding: 4px 8px;
    border-radius: 16px;
    font-size: 11px;
    font-weight: 600;
    white-space: nowrap;
    flex-shrink: 0;
}

.provider-badge {
    background: rgba(16, 185, 129, 0.2);
    color: var(--green);
}

.model-badge {
    background: rgba(59, 130, 246, 0.2);
    color: var(--primary);
}

.latency,
.pricing-tier {
    color: var(--subtext);
    font-size: 11px;
    white-space: nowrap;
    flex-shrink: 0;
}

.analysis-actions {
    display: flex;
    gap: 8px;
    flex-wrap: wrap;
    width: 100%;
}

.action-btn {
    padding: 6px 12px;
    background: var(--card-bg);
    border: 1px solid var(--card-border);
    border-radius: 16px;
    cursor: pointer;
    font-size: 11px;
    transition: all 0.3s ease;
    flex: 1;
    min-width: 0;
    text-align: center;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    color: var(--text);
}

.action-btn:hover {
    background: var(--hover);
}

.analysis-body {
    margin-bottom: 15px;
    width: 100%;
    overflow: hidden;
}

.analysis-error {
    background: rgba(249, 115, 22, 0.1);
    color: var(--orange-1);
    padding: 12px;
    border-radius: 8px;
    font-size: 12px;
    line-height: 1.4;
}

.analysis-text {
    line-height: 1.6;
    font-size: 14px;
    word-break: break-word;
    overflow-wrap: break-word;
    hyphens: auto;
    width: 100%;
    color: var(--text);
}

.persian-text {
    direction: rtl;
    text-align: justify;
    text-align-last: right;
}

.english-text {
    direction: ltr;
    text-align: left;
    text-align-last: left;
}

.analysis-footer {
    display: flex;
    flex-direction: column;
    gap: 12px;
    align-items: flex-start;
    padding-top: 12px;
    border-top: 1px solid var(--card-border);
    width: 100%;
}

.quality-rating {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
    width: 100%;
}

.quality-rating span {
    font-size: 13px;
    color: var(--text);
}

.stars {
    display: flex;
    gap: 3px;
}

.star {
    font-size: 18px;
    color: var(--card-border);
    cursor: pointer;
    transition: color 0.3s ease;
    line-height: 1;
}

.star.filled {
    color: var(--orange-1);
}

.btn-export {
    padding: 8px 16px;
    background: var(--green);
    color: white;
    border: none;
    border-radius: 8px;
    cursor: pointer;
    font-size: 13px;
    width: 100%;
    box-sizing: border-box;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

/* Comparative Analysis */
.comparative-analysis {
    background: var(--bg);
    border-radius: 12px;
    padding: 15px;
    margin-top: 15px;
    width: 100%;
    box-sizing: border-box;
    border: 1px solid var(--card-border);
}

.comparative-analysis h4 {
    margin: 0 0 12px 0;
    color: var(--heading);
    font-size: 16px;
}

.comparison-grid {
    display: grid;
    grid-template-columns: 1fr;
    gap: 10px;
    width: 100%;
}

.comparison-item {
    background: var(--card-bg);
    padding: 10px;
    border-radius: 8px;
    display: flex;
    flex-direction: column;
    gap: 4px;
    width: 100%;
    box-sizing: border-box;
}

.comparison-label {
    font-size: 12px;
    color: var(--subtext);
    line-height: 1.2;
    overflow: hidden;
    text-overflow: ellipsis;
}

.comparison-value {
    font-weight: 600;
    color: var(--text);
    font-size: 13px;
    line-height: 1.3;
    overflow: hidden;
    text-overflow: ellipsis;
    word-break: break-word;
}

/* Synthesized Tab */
.synthesized-tab {
    background: linear-gradient(135deg, var(--gradient-1) 0%, var(--gradient-2) 100%);
    color: white;
    font-weight: bold;
    border-left: 4px solid gold;
}

.synthesized-badge {
    margin-left: 5px;
    background: gold;
    color: #333;
    padding: 2px 6px;
    border-radius: 12px;
    font-size: 0.8em;
}

.synthesized-label {
    background: rgba(255, 255, 255, 0.2);
    padding: 2px 8px;
    border-radius: 12px;
    font-size: 0.8em;
    margin-left: 5px;
}

.synthesized-card {
    border: 2px solid var(--primary);
    box-shadow: 0 4px 20px var(--ring);
}

.synthesized-badge-large {
    background: linear-gradient(135deg, var(--gradient-1) 0%, var(--gradient-2) 100%);
    color: white;
    padding: 6px 12px;
    border-radius: 20px;
    font-weight: bold;
    font-size: 0.9em;
}

.synthesized-intro {
    background: rgba(59, 130, 246, 0.1);
    padding: 15px;
    border-radius: 10px;
    margin-bottom: 20px;
    border-right: 4px solid var(--primary);
}

.synthesized-note {
    color: var(--subtext);
    font-size: 0.9em;
    margin-top: 8px;
}

.synthesized-quality {
    color: var(--primary);
    font-weight: bold;
    margin-left: 10px;
}

/* Loading Analysis Steps */
.loading-analysis-steps {
    margin-top: 25px;
}

.loading-step {
    font-size: 1.2em;
    font-weight: 600;
    color: var(--heading);
    margin-bottom: 20px;
    min-height: 40px;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.3s ease;
}

.loading-step.active {
    color: var(--primary);
    animation: pulse 1.5s infinite;
}

@keyframes pulse {
    0% { opacity: 0.7; }
    50% { opacity: 1; }
    100% { opacity: 0.7; }
}

.progress-container {
    background: var(--card-border);
    border-radius: 10px;
    height: 12px;
    margin: 20px auto;
    position: relative;
    overflow: hidden;
    max-width: 500px;
}

.progress-bar {
    background: linear-gradient(90deg, var(--primary), var(--green));
    height: 100%;
    border-radius: 10px;
    transition: width 0.5s ease;
    position: relative;
    overflow: hidden;
}

.progress-bar::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    bottom: 0;
    right: 0;
    background-image: linear-gradient(
        -45deg,
        rgba(255, 255, 255, 0.2) 25%,
        transparent 25%,
        transparent 50%,
        rgba(255, 255, 255, 0.2) 50%,
        rgba(255, 255, 255, 0.2) 75%,
        transparent 75%,
        transparent
    );
    z-index: 1;
    background-size: 50px 50px;
    animation: move 2s linear infinite;
}

@keyframes move {
    0% { background-position: 0 0; }
    100% { background-position: 50px 50px; }
}

.progress-text {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    font-size: 0.8em;
    font-weight: bold;
    color: var(--heading);
    z-index: 2;
}

.step-indicators {
    display: flex;
    justify-content: center;
    gap: 10px;
    margin: 20px 0;
}

.step-dot {
    width: 10px;
    height: 10px;
    border-radius: 50%;
    background: var(--card-border);
    transition: all 0.3s ease;
    cursor: pointer;
}

.step-dot.active {
    background: var(--primary);
    transform: scale(1.2);
    box-shadow: 0 0 10px var(--ring);
}

.estimated-time {
    background: var(--card-bg);
    padding: 8px 15px;
    border-radius: 20px;
    display: inline-block;
    margin: 15px 0;
    font-size: 0.9em;
    color: var(--subtext);
    box-shadow: 0 3px 10px var(--shadow-1);
    animation: timePulse 2s infinite;
}

@keyframes timePulse {
    0%, 100% { transform: scale(1); }
    50% { transform: scale(1.05); }
}

.ai-models-status {
    margin: 20px 0;
}

.models-loading {
    display: inline-flex;
    align-items: center;
    gap: 10px;
    background: rgba(59, 130, 246, 0.1);
    padding: 10px 20px;
    border-radius: 25px;
}

.model-icon {
    font-size: 1.2em;
    animation: modelBounce 2s infinite;
}

@keyframes modelBounce {
    0%, 100% { transform: translateY(0); }
    50% { transform: translateY(-5px); }
}

.model-text {
    font-size: 0.9em;
    color: var(--text);
}

.loading-tips {
    margin-top: 25px;
    padding-top: 20px;
    border-top: 1px dashed var(--card-border);
}

.tips-title {
    font-weight: bold;
    color: var(--orange-1);
    margin-bottom: 5px;
}

.tip-text {
    color: var(--subtext);
    font-size: 0.9em;
    animation: tipFade 5s infinite;
}

@keyframes tipFade {
    0%, 100% { opacity: 0.7; }
    50% { opacity: 1; }
}

/* Formatted Analysis Text */
.formatted-analysis {
    line-height: 1.8;
    text-align: justify;
}

.analysis-heading {
    color: var(--heading);
    font-size: 1.3em;
    font-weight: 700;
    margin: 1.5em 0 0.8em 0;
    padding-bottom: 0.5em;
    border-bottom: 2px solid var(--primary);
    position: relative;
}

.analysis-paragraph {
    margin: 1em 0;
    padding: 0.5em 0;
    text-align: justify;
    line-height: 1.8;
}

.analysis-list {
    margin: 1em 0;
    padding-right: 1.5em;
}

.bullet-point {
    color: var(--orange-1);
    font-weight: bold;
    margin-left: 5px;
}

.list-number {
    color: var(--primary);
    font-weight: bold;
    margin-left: 5px;
}

.number-highlight {
    background: rgba(16, 185, 129, 0.2);
    font-weight: 600;
    color: var(--green);
    padding: 0 3px;
    border-radius: 3px;
    direction: ltr;
    unicode-bidi: embed;
}

.financial-ratio {
    background: rgba(59, 130, 246, 0.08);
    border-right: 3px solid var(--primary);
    padding: 12px 15px;
    margin: 15px 0;
    border-radius: 0 8px 8px 0;
    transition: all 0.3s ease;
}

.financial-ratio:hover {
    background: rgba(59, 130, 246, 0.12);
    transform: translateX(-5px);
}

.ratio-label {
    color: var(--text);
    font-weight: 600;
    display: inline-block;
    min-width: 140px;
}

.ratio-value {
    color: var(--orange-1);
    font-weight: 700;
    direction: ltr;
    unicode-bidi: embed;
}

.formula {
    background: rgba(139, 92, 246, 0.1);
    padding: 2px 6px;
    border-radius: 4px;
    color: var(--gradient-2);
    direction: ltr;
    unicode-bidi: embed;
    font-size: 0.95em;
    margin: 0 2px;
}

/* Analysis Types Controls */
.analysis-types-controls {
    margin-top: 8px;
}

.analysis-buttons {
    display: flex;
    gap: 10px;
    margin-bottom: 15px;
}

.btn-select-all, .btn-deselect-all {
    padding: 6px 12px;
    border: 1px solid var(--card-border);
    background: var(--card-bg);
    border-radius: 6px;
    cursor: pointer;
    font-size: 13px;
    transition: all 0.2s;
    color: var(--text);
}

.btn-select-all:hover {
    background: rgba(16, 185, 129, 0.1);
    border-color: var(--green);
}

.btn-deselect-all:hover {
    background: rgba(249, 115, 22, 0.1);
    border-color: var(--orange-1);
}

.analysis-types-grid {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 10px;
    margin-bottom: 15px;
}

.analysis-type-item {
    display: flex;
    align-items: center;
    padding: 10px;
    border: 2px solid var(--card-border);
    border-radius: 8px;
    cursor: pointer;
    transition: all 0.2s;
    background: var(--card-bg);
}

.analysis-type-item:hover {
    border-color: var(--primary);
    background: rgba(59, 130, 246, 0.05);
}

.analysis-type-item.selected {
    border-color: var(--primary);
    background: rgba(59, 130, 246, 0.1);
}

.analysis-type-checkbox {
    width: 24px;
    height: 24px;
    border: 2px solid var(--card-border);
    border-radius: 4px;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-left: 10px;
    background: var(--card-bg);
}

.analysis-type-item.selected .analysis-type-checkbox {
    border-color: var(--primary);
    background: var(--primary);
}

.checkbox-checked {
    color: white;
    font-weight: bold;
}

.checkbox-unchecked {
    color: var(--muted-2);
}

.analysis-type-label {
    font-size: 14px;
    flex: 1;
    color: var(--text);
}

.selected-types-summary {
    margin-top: 15px;
    padding: 10px;
    background: var(--bg);
    border-radius: 8px;
}

.selected-types-summary small {
    display: block;
    margin-bottom: 8px;
    color: var(--subtext);
}

.selected-types-tags {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
}

.type-tag {
    background: var(--primary);
    color: white;
    padding: 4px 12px;
    border-radius: 16px;
    font-size: 12px;
}

/* Responsive Design */
@media (min-width: 768px) {
    .taraz-analysis-app {
        padding: 20px;
    }
    
    .section-header {
        padding: 20px;
        margin-bottom: 25px;
    }
    
    .section-title {
        font-size: 24px;
    }
    
    .section-subtitle {
        font-size: 15px;
    }
    
    .back-arrow {
        right: 25px;
        top: 25px;
        width: 40px;
        height: 40px;
        font-size: 20px;
    }
    
    .main-content {
        flex-direction: row;
        gap: 25px;
        margin-top: 20px;
    }
    
    .left-panel {
        width: 350px;
        flex-shrink: 0;
    }
    
    .right-panel {
        flex: 1;
        min-width: 0;
    }
    
    .summary-grid {
        grid-template-columns: repeat(3, 1fr);
        gap: 15px;
    }
    
    .summary-item {
        flex-direction: row;
        justify-content: space-between;
        align-items: center;
        padding: 12px;
    }
    
    .analyses-header {
        flex-direction: row;
        justify-content: space-between;
        align-items: center;
    }
    
    .analysis-header {
        flex-direction: row;
        justify-content: space-between;
        align-items: center;
    }
    
    .analysis-footer {
        flex-direction: row;
        justify-content: space-between;
        align-items: center;
    }
    
    .btn-export {
        width: auto;
        padding: 10px 20px;
    }
    
    .comparison-grid {
        grid-template-columns: repeat(3, 1fr);
    }
    
    .comparison-item {
        flex-direction: row;
        justify-content: space-between;
        align-items: center;
        padding: 15px;
    }
}

@media (min-width: 1200px) {
    .summary-grid {
        grid-template-columns: repeat(5, 1fr);
    }
}

@media (max-width: 768px) {
    .analysis-types-grid {
        grid-template-columns: 1fr;
    }
    
    .analysis-type-item {
        padding: 12px;
    }
}

@media (max-width: 360px) {
    .taraz-analysis-app {
        padding: 10px;
    }
    
    .section-title {
        font-size: 20px;
    }
    
    .summary-grid {
        grid-template-columns: 1fr;
    }
    
    .analysis-meta {
        flex-direction: column;
        align-items: flex-start;
        gap: 5px;
    }
    
    .analysis-actions {
        flex-direction: column;
    }
    
    .action-btn {
        width: 100%;
    }
}

/* Print Styles */
@media print {
    .left-panel,
    .back-arrow,
    .action-buttons,
    .analysis-actions {
        display: none !important;
    }
    
    .main-content {
        flex-direction: column;
    }
    
    .right-panel {
        margin: 0;
        padding: 0;
    }
    
    .analysis-card {
        page-break-inside: avoid;
    }
}

/* Scrollbar for long analysis */
.analysis-body {
    max-height: 600px;
    overflow-y: auto;
    padding: 0 5px;
}

.analysis-body::-webkit-scrollbar {
    width: 6px;
}

.analysis-body::-webkit-scrollbar-track {
    background: var(--card-border);
    border-radius: 10px;
}

.analysis-body::-webkit-scrollbar-thumb {
    background: var(--muted-2);
    border-radius: 10px;
}

.analysis-body::-webkit-scrollbar-thumb:hover {
    background: var(--muted);
}
</style>